import { useState, useRef, useEffect } from 'react'
import {
  HiOutlineLocationMarker,
  HiOutlineChevronUp,
  HiOutlineChevronDown,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineSearch,
  HiOutlineX,
} from 'react-icons/hi'
import clsx from 'clsx'

// Bottom sheet states
const SHEET_STATES = {
  COLLAPSED: 'collapsed', // Chỉ hiện header (80px)
  HALF: 'half',           // 40% màn hình
  EXPANDED: 'expanded',   // 85% màn hình
}

// Marker type config
const markerTypeOptions = [
  { value: 'custom', label: 'Tự do', color: '#10B981' },
  { value: 'ban', label: 'Bán', color: '#EF4444' },
  { value: 'thue', label: 'Thuê', color: '#3B82F6' },
  { value: 'both', label: 'Cả hai', color: '#8B5CF6' },
]

function MobileMapBottomSheet({
  markers = [],
  selectedMarker,
  onMarkerSelect,
  onMarkerEdit,
  onMarkerDelete,
  filterType = 'all',
  onFilterChange,
  getMarkerColor,
}) {
  const [sheetState, setSheetState] = useState(SHEET_STATES.COLLAPSED)
  const [searchQuery, setSearchQuery] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [startY, setStartY] = useState(0)
  const [currentY, setCurrentY] = useState(0)
  const sheetRef = useRef(null)

  // Calculate sheet height based on state
  const getSheetHeight = () => {
    switch (sheetState) {
      case SHEET_STATES.COLLAPSED:
        return '80px'
      case SHEET_STATES.HALF:
        return '40vh'
      case SHEET_STATES.EXPANDED:
        return '85vh'
      default:
        return '80px'
    }
  }

  // Filter markers
  const filteredMarkers = markers.filter((m) => {
    // Type filter
    if (filterType !== 'all') {
      if (filterType === 'ban' && m.marker_type !== 'ban' && m.marker_type !== 'both') return false
      if (filterType === 'thue' && m.marker_type !== 'thue' && m.marker_type !== 'both') return false
      if (filterType === 'custom' && m.marker_type !== 'custom') return false
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      return (
        m.name?.toLowerCase().includes(query) ||
        m.address?.toLowerCase().includes(query) ||
        m.label_text?.toLowerCase().includes(query)
      )
    }

    return true
  })

  // Get marker counts
  const markerCounts = {
    all: markers.length,
    custom: markers.filter((m) => m.marker_type === 'custom').length,
    ban: markers.filter((m) => m.marker_type === 'ban' || m.marker_type === 'both').length,
    thue: markers.filter((m) => m.marker_type === 'thue' || m.marker_type === 'both').length,
  }

  // Touch handlers for drag
  const handleTouchStart = (e) => {
    setIsDragging(true)
    setStartY(e.touches[0].clientY)
    setCurrentY(e.touches[0].clientY)
  }

  const handleTouchMove = (e) => {
    if (!isDragging) return
    setCurrentY(e.touches[0].clientY)
  }

  const handleTouchEnd = () => {
    if (!isDragging) return
    setIsDragging(false)

    const diff = startY - currentY
    const threshold = 50

    if (diff > threshold) {
      // Swipe up
      if (sheetState === SHEET_STATES.COLLAPSED) {
        setSheetState(SHEET_STATES.HALF)
      } else if (sheetState === SHEET_STATES.HALF) {
        setSheetState(SHEET_STATES.EXPANDED)
      }
    } else if (diff < -threshold) {
      // Swipe down
      if (sheetState === SHEET_STATES.EXPANDED) {
        setSheetState(SHEET_STATES.HALF)
      } else if (sheetState === SHEET_STATES.HALF) {
        setSheetState(SHEET_STATES.COLLAPSED)
      }
    }
  }

  // Toggle sheet state on header click
  const toggleSheet = () => {
    if (sheetState === SHEET_STATES.COLLAPSED) {
      setSheetState(SHEET_STATES.HALF)
    } else if (sheetState === SHEET_STATES.HALF) {
      setSheetState(SHEET_STATES.EXPANDED)
    } else {
      setSheetState(SHEET_STATES.COLLAPSED)
    }
  }

  // Auto expand when marker selected
  useEffect(() => {
    if (selectedMarker && sheetState === SHEET_STATES.COLLAPSED) {
      setSheetState(SHEET_STATES.HALF)
    }
  }, [selectedMarker])

  return (
    <div
      ref={sheetRef}
      className={clsx(
        'fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl z-30 transition-all duration-300 ease-out',
        'md:hidden' // Only show on mobile
      )}
      style={{
        height: getSheetHeight(),
        transform: isDragging ? `translateY(${currentY - startY}px)` : 'translateY(0)',
      }}
    >
      {/* Drag handle */}
      <div
        className="flex justify-center py-3 cursor-grab active:cursor-grabbing"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={toggleSheet}
      >
        <div className="w-10 h-1 bg-gray-300 rounded-full" />
      </div>

      {/* Header - always visible */}
      <div className="px-4 pb-3 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <HiOutlineLocationMarker className="w-5 h-5 text-primary-600" />
            <span className="font-semibold text-gray-900">
              {markers.length} điểm
            </span>
            {filterType !== 'all' && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-primary-100 text-primary-700">
                {markerTypeOptions.find((o) => o.value === filterType)?.label}
              </span>
            )}
          </div>
          <button
            onClick={toggleSheet}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {sheetState === SHEET_STATES.EXPANDED ? (
              <HiOutlineChevronDown className="w-5 h-5 text-gray-500" />
            ) : (
              <HiOutlineChevronUp className="w-5 h-5 text-gray-500" />
            )}
          </button>
        </div>
      </div>

      {/* Content - hidden when collapsed */}
      {sheetState !== SHEET_STATES.COLLAPSED && (
        <div className="flex flex-col h-[calc(100%-80px)]">
          {/* Search */}
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Tìm kiếm..."
                className="w-full pl-9 pr-9 py-2.5 text-sm bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full"
                >
                  <HiOutlineX className="w-3 h-3 text-gray-500" />
                </button>
              )}
            </div>
          </div>

          {/* Filter tabs */}
          <div className="p-3 border-b border-gray-100">
            <div className="flex gap-1 p-1 bg-gray-100 rounded-xl overflow-x-auto">
              {[
                { value: 'all', label: 'Tất cả', count: markerCounts.all },
                { value: 'custom', label: 'Tự do', color: '#10B981', count: markerCounts.custom },
                { value: 'ban', label: 'Bán', color: '#EF4444', count: markerCounts.ban },
                { value: 'thue', label: 'Thuê', color: '#3B82F6', count: markerCounts.thue },
              ]
                .filter((tab) => tab.value === 'all' || tab.count > 0)
                .map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => onFilterChange?.(tab.value)}
                    className={clsx(
                      'flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap',
                      filterType === tab.value
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600'
                    )}
                  >
                    {tab.color && (
                      <span
                        className="inline-block w-2 h-2 rounded-full mr-1.5"
                        style={{ backgroundColor: tab.color }}
                      />
                    )}
                    {tab.label}
                    <span className="ml-1 text-gray-400">({tab.count})</span>
                  </button>
                ))}
            </div>
          </div>

          {/* Marker list */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            {filteredMarkers.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <HiOutlineLocationMarker className="w-8 h-8 text-gray-300" />
                </div>
                <p className="text-sm text-gray-500">
                  {searchQuery ? 'Không tìm thấy kết quả' : 'Chưa có marker nào'}
                </p>
              </div>
            ) : (
              <div className="p-2 space-y-1">
                {filteredMarkers.map((marker) => (
                  <div
                    key={marker.id}
                    className={clsx(
                      'p-3 rounded-xl transition-all active:scale-[0.98]',
                      selectedMarker?.id === marker.id
                        ? 'bg-primary-50 ring-1 ring-primary-200'
                        : 'bg-white hover:bg-gray-50'
                    )}
                    onClick={() => onMarkerSelect?.(marker)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Thumbnail */}
                      <div
                        className="w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center"
                        style={{ backgroundColor: `${getMarkerColor?.(marker) || '#6B7280'}15` }}
                      >
                        {marker.image_url ? (
                          <img
                            src={marker.image_url}
                            alt={marker.name}
                            className="w-full h-full object-cover rounded-xl"
                            onError={(e) => {
                              e.target.style.display = 'none'
                            }}
                          />
                        ) : (
                          <HiOutlineLocationMarker
                            className="w-5 h-5"
                            style={{ color: getMarkerColor?.(marker) || '#6B7280' }}
                          />
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 text-sm truncate">
                          {marker.name}
                        </h4>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: getMarkerColor?.(marker) || '#6B7280' }}
                          />
                          <span className="text-xs text-gray-500">
                            {markerTypeOptions.find((o) => o.value === marker.marker_type)?.label}
                          </span>
                        </div>
                        {marker.price_text && (
                          <p className="text-xs font-semibold text-red-600 mt-1 truncate">
                            {marker.price_text}
                          </p>
                        )}
                        {marker.address && (
                          <p className="text-xs text-gray-400 mt-0.5 truncate">
                            {marker.address}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            onMarkerEdit?.(marker)
                          }}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <HiOutlinePencil className="w-4 h-4 text-gray-500" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            onMarkerDelete?.(marker.id)
                          }}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <HiOutlineTrash className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default MobileMapBottomSheet
